<template>
  <div class="assets-collection">
    <v-navigation-drawer
      :value="showAssetsCollection"
      app
      right
      clipped
      floating
      class="px-4 mb-12 pt-7"
      :color="isDarkTheme ? 'grayf5' : '#f1f1f1'"
      :width="showCriteria ? '50%' : '495'"
      mobile-breakpoint="990"
      disable-resize-watcher
    >
      <BaseButton
        :has-icon="true"
        class="mb-6"
        :class="{ 'app-apply-blur' : isAdminEditing }"
        @click="hideAssetsCollection"
      >
        <v-icon>close</v-icon>
      </BaseButton>

      <template v-if="effectiveTradeType == 'dynamic'">
        <DynamicTradeCriteria
          v-if="showCriteria"
        />
        <template v-if="draftMovementId && !showAssetsCollectionConfirm">
          <div class="color--text-black-054">
            <h2>Negociação Simulada</h2>
            <p class="body-1">
              Simulação criada de acordo com os critérios selecionados
            </p>
            <v-divider class="my-4" />
          </div>
          <div class="app-align-items-center mb-6">
            <v-spacer />
            <div
              class="d-flex text-caption text-right"
              :class="{ 'app-apply-blur' : isAdminEditing }"
            >
              <div>
                <p class="font-weight-bold primary--text">
                  Valor total selecionado
                </p>
                <p class="font-weight-bold primary--text text-h4">
                  {{ programDraftMovementAmount | formatCurrency }}
                </p>
              </div>
            </div>
          </div>

          <Warning
            v-if="showReportMessage"
            icon="notifications"
            type="info"
            class="ma-1"
          >
            O relatório está sendo gerado e em breve estará disponível na página de Relatórios,
            <a
              @click="goToReports()"
            >
              clique aqui para ir
            </a>.
          </Warning>

          <div
            v-if="(fidcList && fidcList.length) || isDynamicV2"
            class="app-align-items-center"
          >
            <v-autocomplete
              v-if="!isDynamicV2 && destinationField && destinationField.datasource && destinationField.datasource.includes('members')"
              id="qa-id-name-organization-list"
              v-model="fidcSelected"
              :items="fidcList"
              clearable
              item-value="id"
              item-text="nameAndTaxnumber"
              return-object
              :label="destinationField.name"
              hint="Filtre por Razão Social ou CNPJ"
              no-data-text="Organização não encontrada"
              persistent-hint
              :rules="[isValid]"
              style="max-width: 350px;"
              class="my-6"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.tax_number | maskCNPJ }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-select
              v-else-if="!isDynamicV2 && destinationField"
              v-model="fidcSelected"
              :items="fidcList"
              item-text="name"
              item-value="value"
              return-object
              :label="destinationField.name"
            />
            <div v-if="termExtensionEnabled && !showAssetsCollectionConfirm" class="mt-4" :class="{ 'app-apply-blur' : isAdminEditing }">
              <v-select
                v-model="extensionDaysSelected"
                :items="extensionDaysOptions"
                item-text="text"
                item-value="value"
                :disabled="extensionDaysOptions.length === 0"
                label="Dias de alongamento"
                hide-details
                dense
              />
            </div>
            <v-spacer />
            <BaseButton
              v-if="(fidcList && fidcList.length) || isDynamicV2"
              type="text"
              color="blueff"
              class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0 text-decoration-underline text-uppercase"
              button-class="app-font-regular"
              :is-disabled="loadPage"
              @click="executeExportData"
            >
              Exportar
            </BaseButton>
          </div>
          <h4 class="mt-4 color--text-black-054">
            Segmentação de ativos e valor
          </h4>
        </template>
      </template>

      <template v-if="effectiveTradeType == 'select'">
        <template v-if="draftMovementId && !showAssetsCollectionConfirm">
          <div class="app-align-items-center mb-6">
            <v-spacer />
            <div
              class="d-flex text-caption text-right"
              :class="{ 'app-apply-blur' : isAdminEditing }"
            >
              <div>
                <p class="font-weight-bold primary--text">
                  Valor da negociação
                </p>
                <p class="font-weight-bold primary--text text-h4">
                  {{ programDraftMovementAmount | formatCurrency }}
                </p>
              </div>
              <div class="align-self-end pl-2">
                <v-icon class="assets-collection__header--icon primary--text">
                  storefront
                </v-icon>
              </div>
            </div>
          </div>
          <Warning
            v-if="!hasBankAccount"
            class="mx-1"
            icon="warning"
            type="warning"
            theme="light"
          >
            <p :class="{ 'app-apply-blur' : isAdminEditing }">
              <strong>Conta bancária:</strong> Cadastre uma conta
              bancária no perfil da sua organização antes de realizar uma negociação.
              <a
                class=" app-font-semibold text-decoration-underline"
                :href="createLinkToBankAccount"
              >
                Cadastrar conta.
              </a>
            </p>
          </Warning>

          <v-menu
            v-else-if="hasBankAccount && accountSelected"
            v-model="menu"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                style="font-size: 12px"
                class="d-flex assets-collection__accounts-menu color--text-black-054 pa-1 mx-1"
              >
                <div class="d-flex align-start">
                  <v-icon>account_balance_wallet</v-icon>
                  <span class="font-weight-bold">Conta Desembolso</span>
                </div>
                <v-spacer />
                <div
                  class="d-flex flex-column align-end pr-6"
                  :class="{ 'app-apply-blur' : isAdminEditing }"
                >
                  <p>Código Banco</p>
                  <p
                    class="body-2 font-weight-bold truncate-content-text"
                    style="max-width: 88px;"
                  >
                    {{ getBankCodeAndName(accountSelected) }}
                  </p>
                </div>
                <div
                  class="d-flex flex-column align-end pr-6"
                  :class="{ 'app-apply-blur' : isAdminEditing }"
                >
                  <p>Agência</p>
                  <p class="body-2 font-weight-bold">
                    {{ getAgencyAndDigit(accountSelected) }}
                  </p>
                </div>
                <div
                  class="d-flex flex-column align-end"
                  :class="{ 'app-apply-blur' : isAdminEditing }"
                >
                  <p>Conta</p>
                  <p class="body-2 font-weight-bold">
                    {{ getNumberAndDigit(accountSelected) }}
                  </p>
                </div>
                <BaseButton
                  type="text"
                  context-button="program"
                  button-class="my-0"
                  :has-icon="true"
                  v-bind="attrs"
                  :class="{ 'app-apply-blur' : isAdminEditing }"
                  v-on="on"
                >
                  <v-icon color="primary">
                    expand_more
                  </v-icon>
                </BaseButton>
              </div>
            </template>
            <v-list>
              <template v-for="bankInfo in programBankAccounts">
                <div
                  :key="bankInfo.id"
                  style="font-size:12px"
                  class="d-flex assets-collection__accounts-menu--select color--text-black-054 justify-end px-4 py-1"
                  @click="selectAccount(bankInfo)"
                >
                  <BaseDataBank
                    :has-icon="false"
                    :bank-value="getBankCodeAndName(bankInfo)"
                    :agency-value="getAgencyAndDigit(bankInfo)"
                    :account-value="getNumberAndDigit(bankInfo)"
                    style="border: none; width: 100%; padding: 0px!important"
                  />
                </div>
              </template>
            </v-list>
          </v-menu>
        </template>
        <!-- Dias de alongamento (SELECT) -->
        <div v-if="termExtensionEnabled && !showAssetsCollectionConfirm" class="mt-4" :class="{ 'app-apply-blur' : isAdminEditing }">
          <v-select
            v-model="extensionDaysSelected"
            :items="extensionDaysOptions"
            item-text="text"
            item-value="value"
            :disabled="extensionDaysOptions.length === 0"
            label="Dias de alongamento"
            hide-details
            dense
          />
        </div>
      </template>

      <template v-if="draftMovementId && !showAssetsCollectionConfirm && !showCriteria">
        <div class="assets-collection__actions--container mt-6 mx-1 mb-2">
          <v-progress-linear
            v-if="sendingTrade"
            indeterminate
            color="primary"
          />
          <div
            class="assets-collection__actions d-flex align-center justify-space-between"
          >
            <p
              class="font-weight-bold text-body-2 primary--text pl-6"
            >
              {{ showTotalOfAssetsSelected }} selecionados
            </p>
            <div :class="{ 'app-apply-blur' : isAdminEditing }">
              <BaseButton
                type="text"
                class="primary--text app-font-semibold"
                :is-disabled="sendingTrade"
                @click="deleteDraftMovement"
              >
                Excluir
              </BaseButton>
              <BaseButton
                v-if="isTradeDynamic"
                color="primary"
                class="white--text app-font-semibold"
                :is-disabled="isButtonDisabled || sendingTrade"
                @click="sendTradeDynamic"
              >
                Enviar
              </BaseButton>
              <BaseButton
                v-else
                color="primary"
                class="white--text app-font-semibold"
                :is-disabled="isButtonDisabled"
                @click="() => showAssetsCollectionConfirm = true"
              >
                Avançar
              </BaseButton>
            </div>
          </div>
          <BaseDataTable
            :headers="tableAssetHeaders"
            :hide-headers="true"
            :is-admin-editing="isAdminEditing"
            :items="dataDraftMovementsChildren"
            class="color--text-black-054"
            :options="pagination"
          >
            <template v-slot:inject-header="{headers}">
              <tr>
                <template
                  v-for="(header, index) in headers.props.headers"
                >
                  <th
                    v-if="header.value != 'admin-config'"
                    :key="index"
                    style="white-space: nowrap;"
                    :class="{ 'header-sortable': header.value === pagination.sort_by }"
                    @click="header.value === pagination.sort_by ? changeSort() : null"
                  >
                    {{ translateRoleType(header.text) }}
                    <v-icon
                      v-if="header.value === pagination.sort_by"
                      small
                    >
                      {{ pagination.sort_order === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </v-icon>
                  </th>
                  <th
                    v-if="header.value == 'admin-config'"
                    :key="index"
                    class="text-right"
                    :class="header.class"
                  />
                </template>
              </tr>
            </template>
            <template v-slot:inject-items="scopedItems">
              <tr
                v-for="props in scopedItems"
                :key="props.item.id"
              >
                <template
                  v-for="header, index in tableAssetHeaders"
                >
                  <td
                    v-if="header.value != 'admin-config'"
                    :key="index"
                    class="text-left"
                  >
                    <div>
                      {{ formatFields(header.value, props.item, header.input_type) }}
                    </div>
                  </td>
                  <td
                    v-if="header.value == 'admin-config'"
                    :key="index"
                    class="text-right"
                  >
                    <BaseButton
                      v-if="effectiveTradeType === 'select'"
                      type="text"
                      :has-icon="true"
                      @click="$emit('deleteDraftMovementChild', props.item.score)"
                    >
                      <v-icon class="primary--text">
                        delete
                      </v-icon>
                    </BaseButton>
                  </td>
                </template>
              </tr>
            </template>
          </BaseDataTable>
        </div>
      </template>

      <AssetsCollectionConfirm
        v-if="showAssetsCollectionConfirm"
        :total-of-assets="showTotalOfAssetsSelected"
        :amount="programDraftMovementAmount"
        :bank-account-or-trading-partner="bankAccountOrTradingPartner"
        :trade-type="effectiveTradeType"
        :installments="simulationInstallments"
        :extension-days="validExtensionDays"
        :term-extension-enabled="termExtensionEnabled"
        @back-page="backPage"
        @close-assets-collection="hideAssetsCollection"
        @updateAssetList="updateAssetList"
        @showError="showError"
      />
    </v-navigation-drawer>
    <BaseSnackbar
      :active-snackbar="feedback"
      :type-snackbar="feedbackColor"
      :text-snackbar="feedbackMessage"
      @closeSnackbar="feedback = !feedback"
    />
  </div>
</template>

<script>

import store from '../../store'
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex'
import BaseSnackbarConfiguration from '../../mixins/BaseSnackbarConfiguration.js'
import BaseSnackbar from '../BaseSnackbar.vue'
import BaseDataTable from '../BaseDataTable.vue'
import AssetsCollectionConfirm from './AssetsCollectionConfirm'
import BaseDataBank from '../BaseDataBank.vue'
import UtilsBankAccount from '../../mixins/UtilsBankAccount'
import Warning from '../Program/Warning.vue'
import ProgramRoles from "@/mixins/ProgramRoles"
import DynamicTradeCriteria from './DynamicTradeCriteria'

export default {
  components: {BaseDataTable, AssetsCollectionConfirm, BaseDataBank, Warning, DynamicTradeCriteria, BaseSnackbar},
  mixins: [UtilsBankAccount, ProgramRoles, BaseSnackbarConfiguration],
  props: {
    showAssetsCollection: {
      type: Boolean,
      required: true
    },
    isAdminEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    tradeType: {
      type: String,
      required: true
    },
    draftMovementId: {
      type: String,
      required: false,
      default: () => ''
    }
  },
  data() {
    return {
      showAssetsCollectionConfirm: false,
      fieldsAssetColumn: [],
      tableAssetHeaders: [],
      menu: false,
      accountSelected: null,
      showAccountList: false,
      destinationField: {},
      destinationSchemaLoaded: false,
      fidcList: [],
      fidcSelected: null,
      buyerOrganizationList: [],
      sellerOrganizationList: [],
      tradingPartnerOrganizationList: [],
      showReportMessage: false,
      extensionDaysSelected: null,
      extensionDaysOptions: [],
      sendingTrade: false,
      pagination: {
        sort_by: "number_of_installment",
        sort_order: "asc"
      }
    }
  },
  computed: {
    ...mapState({
      accountId: state => state.auth.keycloak.tokenParsed['account-id'],
      programDraftMovementAmount: state => state.programDraftMovements.programDraftMovementAmount,
      programDraftMovementId: state => state.programDraftMovements.programDraftMovementId,
      programDraftMovementData: state => state.programDraftMovements.programDraftMovementData,
      programDraftMovementsChildren: state => state.programDraftMovements.programDraftMovementsChildren,
      programDraftMovementTotalAssets: state => state.programDraftMovements.programDraftMovementTotalAssets,
      suggestions: state => state.programFilters.suggestions,
      programMembers: state => state.programMembers.programMembers,
      loadPage: (state) => state.loadPage
    }),
    ...mapState('programMovements', ['programBankAccounts']),
    ...mapGetters({ isTradeDynamicFor: 'isTradeDynamic' }),
    dataDraftMovementsChildren() {
      const items = this.programDraftMovementsChildren ? this.programDraftMovementsChildren.map(element => {
        const {type, id, score, data} = element
        const asset = {
          type,
          score,
          ...data
        }
        return asset
      }) : []

      const hasSortColumn = this.tableAssetHeaders.some(header => header.value === this.pagination.sort_by)
      if (!hasSortColumn) return items

      const key = this.pagination.sort_by
      const order = this.pagination.sort_order === 'asc' ? 1 : -1
      return items.sort((a, b) => String(a[key]).localeCompare(String(b[key]), undefined, { numeric: true }) * order)
    },
    simulationInstallments() {
      const data = this.programDraftMovementData || {}
      const key = Object.keys(data).find(k =>
        k.indexOf('criteria_') === 0 &&
        (k.toLowerCase().indexOf('installment') !== -1 || k.toLowerCase().indexOf('parcela') !== -1)
      )
      if (key) {
        const value = data[key]
        return Array.isArray(value) ? value[value.length - 1] : value
      }
      return null
    },
    firstColumnKey() {
      const header = this.tableAssetHeaders.find(header => header.value !== 'admin-config')
      return header ? header.value : null
    },
    isDynamicV2() {
      return this.tradeType === 'dynamic_v2'
    },
    ...mapGetters('programDraftMovements', ['isDraftSimulated']),
    effectiveTradeType() {
      if (!this.isDynamicV2) return this.tradeType
      return this.draftMovementId && !this.isDraftSimulated ? 'select' : 'dynamic'
    },
    bankAccountOrTradingPartner() {
      if (this.isDynamicV2) {
        return this.effectiveTradeType === 'select' ? this.accountSelected : null
      }
      return this.accountSelected || this.fidcSelected
    },
    showCriteria() {
      return this.effectiveTradeType === 'dynamic' && !this.draftMovementId
    },
    showTotalOfAssetsSelected() {
      return this.programDraftMovementTotalAssets || this.programDraftMovementsChildren.length;
    },
    hasBankAccount() {
      return this.programBankAccounts.length > 0
    },
    isButtonDisabled() {
      if (this.showTotalOfAssetsSelected == 0) return true
      if (this.effectiveTradeType === 'select') {
        return !this.hasBankAccount
      }
      if (this.effectiveTradeType === 'dynamic' && (this.isDynamicV2 || !this.fidcList || !this.fidcList.length)) {
        return false
      }
      return (!this.hasBankAccount && !this.fidcSelected)
    },
    roleOrganizationId() {
      return this.getRoleOrganizationId()
    },
    createLinkToBankAccount() {
      const url = window.location.hostname
      let index = url.indexOf('.')
      const newUrl = url.slice(index + 1)
      return `https://${newUrl}/#/platform-organization/${this.roleOrganizationId}/bank-account`
    },
    programConfiguration () {
      const s = store && store.state && store.state.platformProgram
      return s && s.programInfo ? s.programInfo.configuration : null
    },
    isTradeDynamic() {
      return this.isTradeDynamicFor(this.effectiveTradeType)
    },
    termExtensionEnabled () {
      const config = this.programConfiguration
      const raw = config && config.enable_term_extension

      if (typeof raw === 'boolean') return raw
      if (typeof raw === 'number')  return raw === 1
      if (typeof raw === 'string') {
        const v = raw.trim().toLowerCase()
        return v === 'true' || v === '1' || v === 'yes' || v === 'sim'
      }
      return false
    },
    termExtensionRawList () {
      return this.programConfiguration ? this.programConfiguration.term_extension_days : null
    },
    validExtensionDays () {
      return (this.termExtensionEnabled &&
              Number.isInteger(this.extensionDaysSelected) &&
              this.extensionDaysSelected >= 1)
        ? this.extensionDaysSelected
        : null
    }
  },
  methods: {
    ...mapActions('programMovements', ['apiGetProgramBankAccounts', 'apiGetSchemas', 'apiPatchProgramMovements']),
    ...mapActions('programDraftMovements',
      ['apiGetDraftMovementsChildren', 'apiDeleteDraftMovement', 'apiUpdateDraftMovement']),
    ...mapActions("programMembers", ["apiGetProgramMembers"]),
    ...mapMutations('programDraftMovements', ['setDraftMovementsChildren']),
    ...mapMutations(["setDisableNavigation"]),
    changeSort(){
      this.pagination.sort_order = this.pagination.sort_order === 'asc' ? 'desc' : 'asc'
    },
    parseHeadersAssetTable(){
      let tableAssetColumn = this.fieldsAssetColumn.sort((a, b) => a.sequence - b.sequence) || []
      let fields = tableAssetColumn.map(header => {
        return {
          hidden: header.hidden,
          text: header.name,
          value: header.key,
          class: header.key,
          'input_type': header['input_type'],
          sortable: true
        }
      })
      fields.push({
        hidden: false,
        text: '',
        value: 'admin-config',
        class: 'delete-icon',
        sortable: false
      })
      this.tableAssetHeaders = [...fields]
    },
    goToReports(){
      this.setDisableNavigation(false)
      this.$router.push('/files/reports')
    },
    hideAssetsCollection() {
      this.$emit("hideAssetsCollection");
      this.showAssetsCollectionConfirm = false
    },
    updateAssetList(){
      this.$emit('updateAssetList')
    },
    showError(value){
      this.$emit("showError", value)
    },
    async deleteDraftMovement() {
      await this.apiDeleteDraftMovement({type: 'trade'})
      this.setDraftMovementsChildren([])
      this.$emit("hideAssetsCollection");
      this.showAssetsCollectionConfirm = false
    },
    async sendTradeDynamic() {
      if (this.sendingTrade) return
      this.sendingTrade = true
      const destination = this.bankAccountOrTradingPartner
      const data = {
        type: 'trade',
        id: this.programDraftMovementId,
        data: {
          action: 'finish',
          data: destination
            ? {
              'fidc_name': destination.name,
              'fidc_tax_number': destination.tax_number
            }
            : {}
        }
      }
      if (this.validExtensionDays) {
        data.data.data.extension_days = this.validExtensionDays
      }
      try {
        await this.apiUpdateDraftMovement(data)
        this.$emit('updateAssetList')

        await new Promise(resolve => setTimeout(resolve, 11000))
        this.$emit('updateAssetList')

        this.hideAssetsCollection()
        this.setDisableNavigation(false)

        if (this.$route.path !== '/trades') {
          this.$router.push('/trades')
        }
      } catch (error) {
        this.showFeedback({
          message: 'Ocorreu um erro ao enviar a negociação.',
          color: 'error'
        })
      } finally {
        this.sendingTrade = false
      }
    },
    backPage() {
      this.showAssetsCollectionConfirm = false
    },
    selectAccount(account) {
      this.accountSelected = account;
    },
    setPrimaryAccount() {
      this.accountSelected = this.programBankAccounts.filter(
        account => account.primary === true)[0] || this.programBankAccounts[0]
    },
    async getBankAccounts() {
      try {
        await this.apiGetProgramBankAccounts();
        this.setPrimaryAccount()
      } catch (error) {
        throw error;
      }
    },
    async getMemberOrganizationList(field) {
      if(field.datasource.includes('members')){
        await this.apiGetProgramMembers()
        this.programMembers.forEach(member => {
          let newMember = member.organization
          newMember.nameAndTaxnumber = `${newMember.name ? newMember.name.toUpperCase() : ''} - ${newMember.tax_number}`
          if(member.role == 'buyer')
            this.buyerOrganizationList.push(newMember)
          else if(member.role == 'seller')
            this.sellerOrganizationList.push(newMember)
          else if(member.role == 'trading-partner')
            this.tradingPartnerOrganizationList.push(newMember)
        })
      } else if (field.datasource.includes('bank')){
        await this.getBankAccounts()
      }

    },
    async getOptionsList(){
      try {
        if(this.tradeType === 'select' || this.isDynamicV2){
          await this.getBankAccounts()
        }
        if (this.tradeType === 'dynamic') {
          await this.loadDestinationOptions()
        }
      } catch (error) {
        this.showFeedback({
          message: 'Erro ao carregar opções da negociação',
          color: 'error'
        })
      }
    },
    async loadDestinationOptions(){
      if (this.destinationSchemaLoaded) return
      this.destinationSchemaLoaded = true
      const field = await this.apiGetSchemas({ scope: 'destination' })
      const destination = field && field.data && field.data.fields && field.data.fields[0]
      if (!destination || !destination.datasource) {
        this.destinationField = {}
        this.fidcList = []
        return
      }
      this.destinationField = destination
      await this.getMemberOrganizationList(this.destinationField)

      let optionsList = {
        "/v1/members?role=buyer": this.buyerOrganizationList,
        "/v1/members?role=seller": this.sellerOrganizationList,
        "/v1/members?role=trading-partner": this.tradingPartnerOrganizationList,
        "/v1/programs/roles": this.getRoleOrganizations(),
        "/v1/movements/assets/suggestions/values": this.suggestions,
        "/v1/bank_accounts": this.programBankAccounts
      }
      if(this.destinationField.datasource.startsWith('[')){
        this.fidcList = JSON.parse(this.destinationField.datasource)
      } else {
        this.fidcList = optionsList[this.destinationField.datasource] || []
      }
    },
    async executeExportData() {
      const { total_assets, amount, id, ...criteriaPayload} = this.programDraftMovementData
      const data = {
        action: 'simulate-report',
        data: {
          ...criteriaPayload
        }
      }
      await this.apiPatchProgramMovements({category: 'assets', data})
      this.showReportMessage = true
    },
    normalizeExtensionDays () {
      const raw = this.termExtensionRawList
      let list = []
      try {
        const base = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : [])
        list = (base || [])
          .map(v => (typeof v === 'string' ? parseInt(v, 10) : v))
          .filter(v => Number.isInteger(v) && v >= 1)
        const seen = new Set()
        list = list.filter(v => (seen.has(v) ? false : (seen.add(v), true)))
      } catch (_) {
        list = []
      }

      if (this.termExtensionEnabled && list.length === 0) {
        console.warn('[TermExtension] term_extension_days inválido/vazio. Config:', raw)
      }

      const items = list.map(n => ({ text: String(n), value: n }))
      this.extensionDaysOptions = [...items, { text: 'Nenhum', value: null }]
      if (this.extensionDaysOptions.length > 0) {
        this.extensionDaysSelected = this.extensionDaysOptions[0].value
      }

      if (!(Number.isInteger(this.extensionDaysSelected) && this.extensionDaysSelected >= 1)) {
        this.extensionDaysSelected = null
      }
    },
  },
  watch: {
    termExtensionEnabled (on) {
      if (!on) {
        this.extensionDaysSelected = null
        this.extensionDaysOptions = []
      } else {
        this.normalizeExtensionDays()
      }
    },
    termExtensionRawList () { this.normalizeExtensionDays() },
    tradeType(value) {
      if(value){
        this.getOptionsList()
      }
    }
  },
  async mounted() {
    if (this.programDraftMovementId) {
      this.apiGetDraftMovementsChildren({type: 'trade', id: this.programDraftMovementId})
    }
    let result = await this.apiGetSchemas({ scope: 'criteria-table' })
    this.fieldsAssetColumn = result.data && result.data.fields || []
    this.parseHeadersAssetTable()
    this.getOptionsList()
    this.normalizeExtensionDays()
  },

}
</script>

<style scoped>
.assets-collection__header--icon {
  font-size: 40px
}

.assets-collection__actions {
  background-color: var(--v-grayf8-base);
  padding: 10px 10px 10px;
}

.assets-collection__accounts-menu {
  border: 1px solid rgba(38, 70, 116, 0.102);
  border-radius: 2px;
}

.assets-collection__accounts-menu > div .v-icon {
  font-size: 1.125rem;
}

.assets-collection__accounts-menu--select {
  background: transparent;
}

.assets-collection__accounts-menu--select:hover {
  background: var(--v-grayf4-base);
}

/* TABLE */
.theme--light.v-data-table >>> .v-data-footer {
  border-top: thin solid rgba(0, 0, 0, 0.12) !important;
}

.v-data-table >>> th {
  font-weight: 600;
}

.v-data-table >>> th.header-sortable {
  cursor: pointer;
}

.assets-collection__actions--container {
  box-shadow: 0px 1px 3px #00000061;
}

.delete-icon {
  width: 40px;
}
</style>
